/**
 * Badge Component
 *
 * A versatile status indicator or label with support for icons,
 * pulse animations, and bordered variants.
 *
 * @example
 * // Basic usage
 * <Badge variant="success">Active</Badge>
 *
 * // With icon
 * <Badge variant="purple" icon={<SparklesIcon className="w-3 h-3" />}>
 *   AI Generated
 * </Badge>
 *
 * // With pulse animation
 * <Badge variant="error" pulse>Live</Badge>
 */

import * as React from 'react';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'purple';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  /** Badge content */
  children?: React.ReactNode;
  /** Color variant */
  variant?: BadgeVariant;
  /** Size */
  size?: BadgeSize;
  /** Show as dot only */
  dot?: boolean;
  /** Optional className */
  className?: string;
  /** Icon to display before text */
  icon?: React.ReactNode;
  /** Show animated pulse dot before text */
  pulse?: boolean;
  /** Show border (default: true for stitch design match) */
  bordered?: boolean;
  /** Render the badge as a native button for interactive popover triggers. */
  interactive?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * Design system color variants using stitch colors
 * - Primary: #3b82f6 (blue)
 * - Success: #10b981 (emerald green)
 * - Warning: #f59e0b (amber)
 * - Error: #ef4444 (red)
 * - Purple: #8b5cf6 (for AI/prep features)
 */
const variantClasses: Record<BadgeVariant, { base: string; border: string; dot: string }> = {
  default: {
    base: 'bg-white/10 text-white/70',
    border: 'border-white/20',
    dot: 'bg-white/70',
  },
  primary: {
    base: 'bg-[#3b82f6]/20 text-[#3b82f6]',
    border: 'border-[#3b82f6]/30',
    dot: 'bg-[#3b82f6]',
  },
  success: {
    base: 'bg-[#10b981]/20 text-[#10b981]',
    border: 'border-[#10b981]/30',
    dot: 'bg-[#10b981]',
  },
  warning: {
    base: 'bg-[#f59e0b]/20 text-[#f59e0b]',
    border: 'border-[#f59e0b]/30',
    dot: 'bg-[#f59e0b]',
  },
  error: {
    base: 'bg-[#ef4444]/20 text-[#ef4444]',
    border: 'border-[#ef4444]/30',
    dot: 'bg-[#ef4444]',
  },
  info: {
    base: 'bg-[#3b82f6]/20 text-[#3b82f6]',
    border: 'border-[#3b82f6]/30',
    dot: 'bg-[#3b82f6]',
  },
  purple: {
    base: 'bg-[#8b5cf6]/20 text-[#8b5cf6]',
    border: 'border-[#8b5cf6]/30',
    dot: 'bg-[#8b5cf6]',
  },
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-[11px] px-2 py-0.5 gap-1 font-medium',
  md: 'text-xs px-2 py-0.5 gap-1.5',
  lg: 'text-sm px-2.5 py-1 gap-1.5',
};

const dotSizeClasses: Record<BadgeSize, string> = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
};

const pulseDotSizeClasses: Record<BadgeSize, string> = {
  sm: 'h-1.5 w-1.5',
  md: 'h-1.5 w-1.5',
  lg: 'h-2 w-2',
};

const iconSizeClasses: Record<BadgeSize, string> = {
  sm: '[&>svg]:w-2.5 [&>svg]:h-2.5',
  md: '[&>svg]:w-3 [&>svg]:h-3',
  lg: '[&>svg]:w-3.5 [&>svg]:h-3.5',
};

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  dot = false,
  className = '',
  icon,
  pulse = false,
  bordered = false,
  interactive = false,
  ariaLabel,
  ariaExpanded,
  onClick,
}: BadgeProps) {
  const variantConfig = variantClasses[variant];
  const borderClass = bordered ? `border ${variantConfig.border}` : '';

  if (dot) {
    return (
      <span
        className={`
          inline-block rounded-full
          ${dotSizeClasses[size]}
          ${variantConfig.dot}
          ${pulse ? 'animate-pulse' : ''}
          ${className}
        `}
      />
    );
  }

  const content = (
    <>
      {pulse && (
        <span
          className={`
            ${pulseDotSizeClasses[size]}
            rounded-full animate-pulse flex-shrink-0
            ${variantConfig.dot}
          `}
        />
      )}
      {icon && !pulse && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </>
  );
  const badgeClassName = `
    inline-flex items-center font-medium rounded-full
    ${variantConfig.base}
    ${borderClass}
    ${sizeClasses[size]}
    ${iconSizeClasses[size]}
    ${className}
  `;

  return interactive ? (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      onClick={onClick}
      className={badgeClassName}
    >
      {content}
    </button>
  ) : (
    <span className={badgeClassName}>{content}</span>
  );
}

export default Badge;
